/*  @flow */

import * as React from 'react';

import { activities } from 'components/Activity/ActivityData';

import ActionButton from 'components/ActionsList/components/ActionButton/ActionButton';

import {
  createReportFault,
  createRequestQuote,
  createRequestOwnership,
  createRequestExtendRental,
  createRequestAllocateProject,
  createRequestAssetCalibration,
  updateExtendRental,
  updateAllocateProject,
  updateAssetCalibration,
  updateEditRentedAsset,
  updateEditOwnedAsset,
  updateReassignOwnership,
  updateArchiveUnarchiveOwned,
  updateRemoveProject,
  updateChangeLocation,
  auditAsset,
} from 'permissions/asset';
import * as statuses from '../../utils/statuses';

export const actionsListDataOwned = [
  {
    label: 'general:actions.edit_asset',
    icon: activities.edited.icon,
    class: '',
    tag: activities.edited.tag,
    action: activities.edited.action,
    type: 'link',
    to: { pathname: '/assets/edit', setStateData: true },
    permissions: updateEditOwnedAsset,
  },
  {
    label: 'general:actions.unassign_asset',
    icon: activities.unassignUser.icon,
    class: '',
    tag: activities.unassignUser.tag,
    action: activities.unassignUser.action,
    permissions: updateReassignOwnership,
    condition(data: Object, action: Object, callback: Function): React.Node {
      const hasUserId = data.user.id;

      return (
        <ActionButton action={action} data={data} onActionDrawer={callback} disabled={!hasUserId} />
      );
    },
  },
  {
    label: 'general:actions.reassign_asset',
    icon: activities.assignedUser.icon,
    class: '',
    tag: activities.assignedUser.tag,
    action: activities.assignedUser.action,
    permissions: updateReassignOwnership,
  },
  {
    label: 'general:actions.request_ownership',
    icon: activities.requestOwnership.icon,
    class: '',
    tag: activities.requestOwnership.tag,
    action: activities.requestOwnership.action,
    permissions: createRequestOwnership,
  },
  {
    label: 'general:actions.allocate_to_project',
    icon: activities.assignedProject.icon,
    class: '',
    tag: activities.assignedProject.tag,
    action: activities.assignedProject.action,
    permissions: [...createRequestAllocateProject, ...updateAllocateProject],
    condition(data: Object, action: Object, callback: Function): React.Node {
      return (
        !data.project && <ActionButton action={action} onActionDrawer={callback} data={data} />
      );
    },
  },
  {
    label: 'general:actions.remove_from_project',
    icon: activities.removedProject.icon,
    class: '',
    tag: activities.removedProject.tag,
    action: activities.removedProject.action,
    permissions: updateRemoveProject,
    condition(data: Object, action: Object, callback: Function): React.Node {
      return data.project && <ActionButton action={action} onActionDrawer={callback} data={data} />;
    },
  },
  {
    label: 'general:actions.request_quote',
    icon: activities.requestQuote.icon,
    class: '',
    tag: activities.requestQuote.tag,
    action: activities.requestQuote.action,
    permissions: createRequestQuote,
  },
  {
    label: 'general:actions.change_location',
    icon: activities.assignedLocation.icon,
    class: '',
    tag: activities.assignedLocation.tag,
    action: activities.assignedLocation.action,
    permissions: updateChangeLocation,
    condition(data: Object, action: Object, callback: Function): React.Node {
      const isDisabled = !data.location;
      return (
        <ActionButton data={data} action={action} onActionDrawer={callback} disabled={isDisabled} />
      );
    },
  },
  {
    label: 'general:actions.archive_asset',
    icon: activities.archived.icon,
    class: '',
    tag: activities.archived.tag,
    action: activities.archived.action,
    permissions: updateArchiveUnarchiveOwned,
    condition(asset: Object, action: Object, callback: Function): React.Node {
      return (
        !asset.archived_at && (
          <ActionButton action={action} data={asset} onActionDrawer={callback} />
        )
      );
    },
  },
  {
    label: 'general:actions.unarchive_asset',
    icon: activities.unarchived.icon,
    class: '',
    tag: activities.unarchived.tag,
    action: activities.unarchived.action,
    permissions: updateArchiveUnarchiveOwned,
    condition(asset: Object, action: Object, callback: Function): React.Node {
      return (
        asset.archived_at && <ActionButton action={action} data={asset} onActionDrawer={callback} />
      );
    },
  },
  {
    label: 'asset:Asset Location Audit',
    icon: activities.assetAudit.icon,
    class: '',
    tag: activities.assetAudit.tag,
    action: activities.assetAudit.action,
    permissions: auditAsset,
    condition(asset: Object, action: Object, callback: Function): React.Node {
      return <ActionButton action={action} data={asset} onActionDrawer={callback} />;
    },
  },
];

export const actionsListDataRented: any = [
  {
    label: 'general:actions.edit_asset',
    icon: activities.edited.icon,
    class: '',
    tag: activities.edited.tag,
    action: activities.edited.action,
    type: 'link',
    to: { pathname: '/assets/edit', setStateData: true },
    permissions: updateEditRentedAsset,
    condition(data: Object, action: Object, callback: Function): React.Node {
      const isSoldAsset = data.status === 'Sold';

      return (
        <ActionButton
          action={action}
          data={data}
          onActionDrawer={callback}
          disabled={isSoldAsset}
        />
      );
    },
  },
  {
    label: 'general:actions.unassign_asset',
    icon: activities.unassignUser.icon,
    class: '',
    tag: activities.unassignUser.tag,
    action: activities.unassignUser.action,
    permissions: updateReassignOwnership,
    condition(data: Object, action: Object, callback: Function): React.Node {
      const hasUserId = data.user.id;

      return (
        <ActionButton action={action} data={data} onActionDrawer={callback} disabled={!hasUserId} />
      );
    },
  },
  {
    label: 'general:actions.reassign_asset',
    icon: activities.assignedUser.icon,
    class: '',
    tag: activities.assignedUser.tag,
    action: activities.assignedUser.action,
    permissions: updateReassignOwnership,
    condition(data: Object, action: Object, callback: Function): React.Node {
      const isReturned = statuses.getCode(data.status) === 'returned';

      return (
        <ActionButton action={action} data={data} onActionDrawer={callback} disabled={isReturned} />
      );
    },
  },
  {
    label: 'general:actions.request_ownership',
    icon: activities.requestOwnership.icon,
    class: '',
    tag: activities.requestOwnership.tag,
    action: activities.requestOwnership.action,
    permissions: createRequestOwnership,
    condition(data: Object, action: Object, callback: Function): React.Node {
      const isSoldAsset = data.status === 'Sold';

      return (
        <ActionButton
          action={action}
          data={data}
          onActionDrawer={callback}
          disabled={isSoldAsset}
        />
      );
    },
  },
  {
    label: 'general:actions.allocate_to_project',
    icon: activities.assignedProject.icon,
    class: '',
    tag: activities.assignedProject.tag,
    action: activities.assignedProject.action,
    permissions: [...createRequestAllocateProject, ...updateAllocateProject],
    condition(data: Object, action: Object, callback: Function): React.Node {
      const isSoldAsset = data.status === 'Sold';
      return (
        !data.project && (
          <ActionButton
            action={action}
            onActionDrawer={callback}
            data={data}
            disabled={isSoldAsset}
          />
        )
      );
    },
  },
  {
    label: 'general:actions.remove_from_project',
    icon: activities.removedProject.icon,
    class: '',
    tag: activities.removedProject.tag,
    action: activities.removedProject.action,
    permissions: updateRemoveProject,
    condition(data: Object, action: Object, callback: Function): React.Node {
      const contractIsClosed = Boolean(
        data && data.contract && data.contract.er_status === 'closed'
      );
      const isSoldAsset = data.status === 'Sold';

      return data.project ? (
        <ActionButton
          action={action}
          data={data}
          onActionDrawer={callback}
          disabled={contractIsClosed || isSoldAsset}
        />
      ) : null;
    },
  },
  {
    label: 'general:actions.extend_rental',
    icon: activities.extensionRequest.icon,
    class: '',
    tag: activities.extensionRequest.tag,
    action: activities.extensionRequest.action,
    permissions: [...createRequestExtendRental, ...updateExtendRental],
    condition(data: Object, action: Object, callback: Function): React.Node {
      const isSoldAsset = data.status === 'Sold';

      return (
        <ActionButton
          action={action}
          data={data}
          onActionDrawer={callback}
          disabled={isSoldAsset}
        />
      );
    },
  },
  {
    label: 'general:actions.request_quote',
    icon: activities.requestQuote.icon,
    class: '',
    tag: activities.requestQuote.tag,
    action: activities.requestQuote.action,
    permissions: createRequestQuote,
    condition(data: Object, action: Object, callback: Function): React.Node {
      const isSoldAsset = data.status === 'Sold';

      return (
        <ActionButton
          action={action}
          data={data}
          onActionDrawer={callback}
          disabled={isSoldAsset}
        />
      );
    },
  },
  {
    label: 'general:actions.change_location',
    icon: activities.assignedLocation.icon,
    class: '',
    tag: activities.assignedLocation.tag,
    action: activities.assignedLocation.action,
    permissions: updateChangeLocation,
    condition(data: Object, action: Object, callback: Function): React.Node {
      let isDisabled = false;
      const isSoldAsset = data.status === 'Sold';
      const contractIsClosed = Boolean(
        data && data.contract && data.contract.er_status === 'closed'
      );

      if (!isSoldAsset) {
        isDisabled = !data.location;
      }

      if (contractIsClosed) {
        isDisabled = true;
      }

      return (
        <ActionButton data={data} action={action} onActionDrawer={callback} disabled={isDisabled} />
      );
    },
  },
  {
    label: 'general:actions.schedule_calibration',
    icon: activities.requestCalibration.icon,
    class: '',
    tag: activities.requestCalibration.tag,
    action: activities.requestCalibration.action,
    permissions: [...createRequestAssetCalibration, ...updateAssetCalibration],
    condition(data: Object, action: Object, callback: Function): React.Node {
      const isSoldAsset = data.status === 'Sold';

      return (
        <ActionButton
          action={action}
          data={data}
          onActionDrawer={callback}
          disabled={isSoldAsset}
        />
      );
    },
  },
  {
    label: 'general:actions.report_fault',
    icon: activities.reportFault.icon,
    class: '',
    tag: activities.reportFault.tag,
    action: activities.reportFault.action,
    permissions: createReportFault,
    condition(data: Object, action: Object, callback: Function): React.Node {
      const isSoldAsset = data.status === 'Sold';

      return (
        <ActionButton
          action={action}
          data={data}
          onActionDrawer={callback}
          disabled={isSoldAsset}
        />
      );
    },
  },
  {
    label: 'asset:Asset Location Audit',
    icon: activities.assetAudit.icon,
    class: '',
    tag: activities.assetAudit.tag,
    action: activities.assetAudit.action,
    permissions: auditAsset,
    condition(asset: Object, action: Object, callback: Function): React.Node {
      const contractIsClosed = Boolean(
        asset && asset.contract && asset.contract.er_status === 'closed'
      );
      const isSoldAsset = asset.status === 'Sold';

      return (
        <ActionButton
          action={action}
          data={asset}
          disabled={contractIsClosed || isSoldAsset}
          onActionDrawer={callback}
        />
      );
    },
  },
];

export const assetActionList = [...actionsListDataRented, ...actionsListDataOwned];
